/**
 * paper-3-html — 지면 파이프라인 ③ 그리기. 채워진 지면(InPaper) → CSS 그리드 HTML.
 *
 * "정본 둘" 의 서식 정본(템플릿 xlsx)에서 뽑은 기하를 지면으로 그린다.
 * 템플릿 xlsx 파싱은 오프라인 추출기(build/extract-template.mjs, exceljs)가 담당하고
 * 그 산출물(quote.*.template.json)만 커밋한다. → 앱·빌드 어디에도 exceljs 의존 없음.
 * 템플릿이 바뀌면 추출기만 한 번 다시 돌려 JSON 을 갱신한다.
 * (설계: docs/QUOTE_ONEPAGE_DESIGN.md §4)
 *
 * 이 파일은 그리기만 한다. 값을 채우는 건 앞 단계(paper-2-fill),
 * 칸·틀 명사는 paper-0-cells, css()·escapeHtml 은 src/lib(기능).
 *
 * 좌표계 등식: 엑셀 열/행 번호 = CSS grid-column/row 번호. 병합은 span.
 * 실물 예시 — 수량 셀 {r:15, c:34, cs:2, s:7, t:"2"} 는:
 *   <div class="pc" style="grid-column:34/span 2;grid-row:15/span 1;…">2</div>
 *
 * ── 모양의 정본이 둘이다 (칸마다 주인이 다름) ──
 *   종이에 있는 칸(일반·편집): 모양의 정본 = 템플릿 xlsx → templateStyleDeclarations 전부를 인라인으로.
 *   종이에 없는 칸(우리 줄) : 모양의 정본 = OUR_LINE_DECLARATIONS(이 파일) → 역시 인라인.
 *   시트(PAPER_CSS)에는 전 셀 공통과 상호작용 상태만 남는다.
 *
 * ── HTML 통신 규약 — 이 파일이 div 에 적어두면 딴 데서 읽는 신호들 ──
 *   class "pc"               지면 셀 공통 (paper cell) — PAPER_CSS 가 읽음
 *   class "pc-ed"            편집 칸 상호작용(호버·심어질 input 꾸밈) — PAPER_CSS
 *   class "pc-srcbtn"        소스 버튼 상호작용(호버) — PAPER_CSS
 *   data-action="qe-edit"    클릭하면 input 을 심어라 — QuoteEditorView 클릭 핸들러 (qe = quote editor)
 *   data-action="qe-source"  클릭하면 소스 팝오버를 열어라 — QuoteEditorView
 *   data-idx/field/kind      몇 번째 행의 · 어느 필드를 · 어떤 입력기로 — QuoteEditorView
 */

import { css, stylesheet, type CssDeclarations } from '../../lib/css-declarations';
import { escapeHtml } from '../../lib/escape-html';
import type { CellStyle, TemplateCell, EditCell, OurLineCell, OurLineLook, FilledCell, InPaper } from './paper-0-cells';

// ─── 셀 하나를 그리는 재료들 — 함수 하나 = 값 하나 상상 가능한 크기 ───

/**
 * 셀 자리 — "엑셀 주소 = 그리드 주소" 등식의 실물.
 * {r:15, c:34, rs:1, cs:2} → { 'grid-column': '34/span 2', 'grid-row': '15/span 1' }
 */
function positionDeclarations(cell: FilledCell): CssDeclarations {
  return {
    'grid-column': `${cell.c}/span ${cell.cs}`,
    'grid-row':    `${cell.r}/span ${cell.rs}`,
  };
}

/**
 * 글꼴 — 엑셀 pt × 4/3 = 화면 px (96dpi ÷ 72pt).
 * {sz:9, b:true, fn:'맑은 고딕'} → { 'font-size': '12.0px', 'font-weight': 700, 'font-family': "'맑은 고딕',…" }
 */
function fontDeclarations(style: CellStyle): CssDeclarations {
  return {
    'font-size':   `${((style.sz * 4) / 3).toFixed(1)}px`,
    'font-weight': style.b && 700,
    'font-family': Boolean(style.fn) && `'${style.fn}','Malgun Gothic',sans-serif`,
  };
}

/**
 * 색 — 글자색·배경색. 없는 쪽은 꺼진 줄.
 * {co:'#FF0000', fl:null} → { color: '#FF0000', background: false }
 */
function colorDeclarations(style: CellStyle): CssDeclarations {
  return {
    color:      style.co ?? false,
    background: style.fl ?? false,
  };
}

/**
 * 정렬 — 엑셀 정렬 단어를 flex 단어로 번역. 셀은 flex 컨테이너다(PAPER_CSS .pc).
 * {al:'right', va:'middle'} → { 'justify-content': 'flex-end', 'align-items': 'center' }
 */
function alignDeclarations(style: CellStyle): CssDeclarations {
  return {
    'justify-content': style.al === 'right' ? 'flex-end' : style.al === 'center' ? 'center' : 'flex-start',
    'align-items':     style.va === 'top' ? 'flex-start' : style.va === 'bottom' ? 'flex-end' : 'center',
  };
}

/**
 * 테두리 — 상·하·좌·우 각각 유무.
 * {bt:true, bb:true} → { 'border-top': '1px solid #1a1a1a', 'border-bottom': '1px solid #1a1a1a', … }
 */
function borderDeclarations(style: CellStyle): CssDeclarations {
  return {
    'border-top':    style.bt && '1px solid #1a1a1a',
    'border-bottom': style.bb && '1px solid #1a1a1a',
    'border-left':   style.bl && '1px solid #1a1a1a',
    'border-right':  style.br && '1px solid #1a1a1a',
  };
}

/**
 * 템플릿(엑셀)이 정한 모양 전부 = 자리 + 글꼴 + 색 + 정렬 + 테두리.
 * 모양의 정본이 템플릿인 칸 — 즉 고객 종이에 실재하는 칸(일반·편집)만 이걸 입는다.
 * 우리 줄은 종이에 없는 칸이라 템플릿 모양이 존재하지 않는다 — 파라미터 타입이
 * 우리 줄 칸을 거부하므로 잘못 부르면 컴파일 에러다.
 */
function templateStyleDeclarations(cell: TemplateCell | EditCell, style: CellStyle): CssDeclarations {
  return {
    ...positionDeclarations(cell),
    ...fontDeclarations(style),
    ...colorDeclarations(style),
    ...alignDeclarations(style),
    ...borderDeclarations(style),
  };
}

/**
 * 편집 칸의 input 정렬 단어 — flex 정렬과 별개로, 심어질 input 이 text-align 을 상속받는다.
 * 'right' → 'right' · 'center' → 'center' · 그 외 → 'left'
 */
function editTextAlign(al: string): 'right' | 'center' | 'left' {
  return al === 'right' ? 'right' : al === 'center' ? 'center' : 'left';
}

// ─── 셀 종류별 div — 편집 칸 / 우리 줄 칸 / 일반 칸 ───

/**
 * 편집 칸 — data-idx/field/kind 좌표를 div 에 박는다. 클릭하면 View(openEditor)가
 * 이 좌표를 읽어 input 을 심는다. 빈 값은 "—" 플레이스홀더.
 * (HTML 속성 이름 data-kind 는 View 와의 규약이라 유지 — 값은 cell.input.)
 * 값 예시:
 *   <div class="pc pc-ed" data-action="qe-edit" data-idx="0" data-field="qty"
 *        data-kind="number" style="…;text-align:right">2</div>
 */
function renderEditCell(cell: EditCell, style: CellStyle, text: string): string {
  const styleCss = css({ ...templateStyleDeclarations(cell, style), 'text-align': editTextAlign(style.al) });
  const inner = escapeHtml(text) || '<span style="color:#c2c9d0">—</span>';
  return `<div class="pc pc-ed" data-action="qe-edit" data-idx="${cell.idx}" data-field="${cell.field}" data-kind="${cell.input}" style="${styleCss}">${inner}</div>`;
}

/** 우리 줄 공통 모양 — 작은 글씨, 세로 가운데. */
const OUR_LINE_BASE_DECLARATIONS: CssDeclarations = {
  font: "11px/1.3 'Malgun Gothic','맑은 고딕',sans-serif",
  'align-items': 'center',
};

/** 우리 칸 종류별 모양 — look 하나가 한 줄. 마진율 4단 색·마진 금액 부호 색의 실물이 여기다(문턱·부호 판정은 fill). */
const OUR_LINE_DECLARATIONS: Record<OurLineLook, CssDeclarations> = {
  'source-linked':  { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-start', color: '#1a7d4d' },
  'source-missing': { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-start', color: '#c0554b' },
  cost:             { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-end',   color: '#8a97a1' },
  'margin-good':    { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-end',   color: '#1a9d63', 'font-weight': 700 },
  'margin-mid':     { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-end',   color: '#c9821b', 'font-weight': 700 },
  'margin-low':     { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-end',   color: '#d0453b', 'font-weight': 700 },
  'margin-none':    { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-end',   color: '#c4ccd2', 'font-weight': 700 },
  // 값행의 마진 금액(틀 밖 열) — 값행 숫자와 같은 급으로 굵게, 자릿수 맞춤(tabular)
  'margin-amount-plus':  { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-end', color: '#1a9d63', 'font-weight': 700, 'font-variant-numeric': 'tabular-nums', 'padding-right': '4px' },
  'margin-amount-minus': { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-end', color: '#d0453b', 'font-weight': 700, 'font-variant-numeric': 'tabular-nums', 'padding-right': '4px' },
  'margin-amount-none':  { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-end', color: '#c4ccd2', 'font-weight': 700, 'font-variant-numeric': 'tabular-nums', 'padding-right': '4px' },
  // 구성 머리 아래 칩 줄 오른쪽 끝에 얹히는 "＋ 항목" 버튼 — 파랑, 오른쪽 정렬(호버는 PAPER_CSS .pc-addbtn)
  'add-items':           { ...OUR_LINE_BASE_DECLARATIONS, 'justify-content': 'flex-end', color: '#2f6fed', 'font-weight': 700, 'padding-right': '6px', cursor: 'pointer' },
};

/**
 * 우리 줄 칸 — 고객 종이에 없는 우리 뷰 전용 덧붙임 줄(소스·원가·마진).
 * 종이에 없는 칸이라 템플릿(엑셀) 모양이 존재하지 않는다 —
 * 모양은 OUR_LINE_DECLARATIONS[look] 가 인라인으로 전부 정한다(자리 + 종류별 모양).
 * 클래스는 상호작용용 pc-srcbtn(호버는 인라인 불가 → PAPER_CSS)만 붙는다.
 * 값 예시: <div class="pc pc-srcbtn" data-action="qe-source" data-idx="0"
 *            style="grid-column:…;font:11px/1.3 …;color:#1a7d4d">재고 #12</div>
 */
function renderOurLineCell(cell: OurLineCell, text: string): string {
  const styleCss = css({ ...positionDeclarations(cell), ...OUR_LINE_DECLARATIONS[cell.look] });
  if (cell.sourceIdx != null) {
    return `<div class="pc pc-srcbtn" data-action="qe-source" data-idx="${cell.sourceIdx}" style="${styleCss}">${escapeHtml(text)}</div>`;
  }
  if (cell.addItemsServerId != null) {
    return `<div class="pc pc-addbtn" data-action="qe-add-open" data-server-id="${cell.addItemsServerId}" style="${styleCss}">${escapeHtml(text)}</div>`;
  }
  if (cell.costHintIdx != null) {
    return `<div class="pc pc-costhint" data-action="qe-cost-hint" data-idx="${cell.costHintIdx}" title="레퍼런스 보기" style="${styleCss}">${escapeHtml(text)}</div>`;
  }
  return `<div class="pc" style="${styleCss}">${escapeHtml(text)}</div>`;
}

/** 일반 칸 — 템플릿 텍스트(또는 주입된 텍스트) 그대로, 템플릿이 정한 모양 전부를 입고. */
function renderTemplateCell(cell: TemplateCell, style: CellStyle, text: string): string {
  return `<div class="pc" style="${css(templateStyleDeclarations(cell, style))}">${escapeHtml(text)}</div>`;
}

/** 셀 한 개 → div 한 개 — kind 로 갈린다. 세 종류가 전부다(컴파일러가 소진 보증). */
function renderCell(cell: FilledCell, style: CellStyle, text: string): string {
  switch (cell.kind) {
    case 'edit':    return renderEditCell(cell, style, text);
    case 'our-line': return renderOurLineCell(cell, text);
    case undefined: return renderTemplateCell(cell, style, text);
  }
}

// ─── 지면 전체 ───

/**
 * 행별 칸 HTML — 그리드 행 하나당 문자열 하나(행 수 = rows.length, 칸 없는 행은 '').
 * 부분 다시 그리기(View paintPaper)의 재료: 행끼리 비교해 달라진 행만 갈아 끼운다.
 * 포장(<div class="paper-row">)은 renderPaper 가 씌운다.
 */
export function renderPaperRows(inPaper: InPaper): string[] {
  const cellsByGridRow = new Map<number, FilledCell[]>();
  let rowCount = inPaper.rows.length;                    // 받은 대로 그린다 — rows 밖 칸도 안 버림
  for (const cell of inPaper.cells) {
    const rowCells = cellsByGridRow.get(cell.r) ?? [];
    rowCells.push(cell);
    cellsByGridRow.set(cell.r, rowCells);
    if (cell.r > rowCount) rowCount = cell.r;
  }
  return Array.from({ length: rowCount }, (_, rowIndex) =>
    (cellsByGridRow.get(rowIndex + 1) ?? [])
      .map((cell) => renderCell(cell, inPaper.styles[cell.s], cell.t ?? ''))
      .join(''));
}

/** 지면을 HTML(CSS 그리드)로. 틀·채워진 지면 어느 쪽이든 받는다.
 *  받은 것만 그린다 — 텍스트를 정하는 건 채움(paper-2)의 일이라 덮어쓰기 입구가 없다
 *  (웹과 xlsx 가 같은 fill 결과를 실어야 하므로).
 *  행마다 .paper-row(display:contents — 그리드 투과) 포장: 부분 다시 그리기의 교체 단위. */
export function renderPaper(inPaper: InPaper): string {
  const gridColumnsCss = inPaper.cols.map((widthPx) => `${widthPx}px`).join(' ');
  const gridRowsCss = inPaper.rows.map((heightPx) => `${heightPx}px`).join(' ');
  const rowDivs = renderPaperRows(inPaper)
    .map((rowCellsHtml, rowIndex) => `<div class="paper-row" data-grid-row="${rowIndex + 1}">${rowCellsHtml}</div>`)
    .join('');

  return `<div class="paper" style="display:grid;grid-template-columns:${gridColumnsCss};grid-template-rows:${gridRowsCss}">${rowDivs}</div>`;
}

/**
 * 지면 시트 — 규칙: 여기엔 ① 전 셀 공통 ② 상호작용 상태(인라인 불가: hover ·
 * 나중에 심어질 input 꾸밈)만 산다. 종류별 정적 모양은 전부 각 render 함수의
 * 인라인(templateStyleDeclarations / OUR_LINE_DECLARATIONS) — 함수를 읽으면 모양이 보이게.
 * 선언부는 인라인과 같은 CssDeclarations 체계 — 셀렉터(:hover 등)만 문자열 키다
 * (csstype 은 선언까지만 타입화하고 셀렉터 문법은 못 한다).
 * 적용 배선: QuoteEditorView 가 <style> 태그로 1회 주입(BASE+PAPER+OUR_VIEW_OUT_PAPER+SOURCE 순)
 * → 이후엔 브라우저가 class 이름으로 매칭한다.
 * 폰트는 시스템(맑은 고딕) — 전 한국 Windows 기본 탑재라 고객 Excel 에서
 * 치환 없이 동일 렌더("고객 뷰 = 고객 Excel" 공짜 성립, 설계 §4.3).
 */
export const PAPER_CSS = stylesheet({
  // ── 전 셀 공통 ──
  '.paper': {
    background:    '#fff',
    width:         'max-content',
    'font-family': "'Malgun Gothic','맑은 고딕',sans-serif",
  },
  '.paper-row': {                     // 행 포장 — 그리드 투과(배치 관여 X), 부분 다시 그리기의 교체 단위
    display: 'contents',
  },
  '.pc': {                            // paper cell — 넘침 숨김·줄바꿈 금지(템플릿 3금지의 CSS판)
    overflow:      'hidden',
    'white-space': 'nowrap',
    padding:       '0 1px',
    'line-height': 1,
    display:       'flex',
  },
  // ── 상호작용 상태 (인라인 불가 항목만) ──
  '.pc-srcbtn': {                     // 소스 버튼
    cursor:          'pointer',
    'border-radius': '2px',
  },
  '.pc-addbtn': {                     // "＋ 항목" 버튼(구성 머리)
    'border-radius': '3px',
  },
  '.pc-addbtn:hover': {
    background: '#eef4ff',
  },
  '.pc-costhint': {                    // 우리 줄 매입 칸 = 매입 후보 버튼
    cursor: 'pointer',
    'border-radius': '2px',
  },
  '.pc-costhint:hover': {
    background: '#eef4ff',
    color: '#2f6fed',
  },
  '.pc-srcbtn:hover': {
    background:  '#eef4ff',
    'box-shadow': 'inset 0 0 0 1px #b9cdf2',
  },
  '.pc-ed': {                         // 편집 칸
    cursor:          'text',
    'border-radius': '2px',
  },
  '.pc-ed:hover': {
    background:  '#eef4ff',
    'box-shadow': 'inset 0 0 0 1px #b9cdf2',
  },
  '.pc-ed input,.pc-ed select': {     // 클릭 후 심어질 입력창 꾸밈
    width:        '100%',
    border:       '1px solid #2f6fed',
    'border-radius': '2px',
    font:         'inherit',
    color:        'inherit',
    'text-align': 'inherit',
    padding:      '0 2px',
    background:   '#fff',
    outline:      'none',
    'box-sizing': 'border-box',
  },
});
